import { Modal, useModals } from 'almanac-ui/Modal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { Data, useCreateFont } from 'hooks/queries/workspaces/useCreateFont'
import { Button } from 'components/Button'
import { useUploadFilesToS3 } from 'hooks/queries/workspaces/useUploadFilesToS3'
import { pluralize } from 'utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import Styles from './ConfirmFontModal.module.scss'
import { useQueryClient } from 'react-query'
import { showToast, Toast } from 'common/ToastMessage'
import { Alert } from 'almanac-ui/icons'
import { FontsModal } from 'components/FontsModal/FontsModal'
import { ConfirmFontModalProps } from './Types'
import { Fragment } from 'react'

const list = (files: File[]) => {
  const names = files.map(({ name }) => name)
  const finalName = names.pop()

  if (!names.length) {
    return <strong>{finalName}</strong>
  }

  return (
    <>
      {names.map((name, index) => (
        <Fragment key={`${name}-${index}`}>
          <strong>{name}</strong>
          {index !== names.length - 1 && ','}
        </Fragment>
      ))}{' '}
      & <strong>{finalName}</strong>
    </>
  )
}

const RejectionMessage = ({ rejections }: { rejections: { message: string }[] }) => (
  <Toast.Root variant="error">
    <Toast.Icon>
      <Alert />
    </Toast.Icon>
    <Toast.Description>
      {rejections.map((rejection, index) => (
        <div key={index}>{rejection.message}</div>
      ))}
    </Toast.Description>
  </Toast.Root>
)

export const ConfirmFontModal = ({ files, openFontsModalOnUpload = true }: ConfirmFontModalProps) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  const { data, isLoading: isLoadingUploadsProject } = useUploadsProject(currentWorkspaceId)
  const { mutateAsync, isLoading: isUploading } = useUploadFilesToS3()
  const { mutateAsync: createFont, isLoading: isCreatingFont } = useCreateFont({ onSuccess: () => {} })
  const { openModal, closeModal } = useModals()
  const handleUpload = async () => {
    if (!data?.project) {
      return
    }

    const toast = showToastMessage(`Uploading ${pluralize('Font', files.length)}`, 'loading')
    const filesData = await mutateAsync({ files, folderId: data.project.id })
    const promises: Promise<Data>[] = []
    filesData.forEach((fileData) => {
      promises.push(createFont({ source_url: fileData.url }))
    })
    const fontResults = await Promise.allSettled(promises)
    const fontData: Data[] = []
    const rejections: { message: string }[] = []
    fontResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        fontData.push(result.value)
      } else {
        rejections.push(result.reason)
      }
    })
    toast()
    if (rejections.length > 0) {
      showToast(<RejectionMessage rejections={rejections} />, 10)
    }
    if (fontData.length > 0) {
      queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'fonts', 'list'])
      closeModal()
      if (openFontsModalOnUpload) {
        openModal(FontsModal)
      }
    }
  }

  return (
    <Modal.Root>
      <Modal.Header
        title={
          isCreatingFont || isUploading
            ? `Uploading ${pluralize('File', files.length)}`
            : 'Please confirm the following'
        }
        headingLevel={4}
      />
      <Modal.Content>
        <p className={Styles.confirmationText}>
          By uploading my own custom {pluralize('font', files.length)}, I acknowledge that I own {list(files)} or have
          the right to use {files.length > 1 ? 'them' : 'it'} for my intended purposes.
        </p>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button onPress={closeModal} size="lg" variant="tertiary">
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            onPress={handleUpload}
            isDisabled={isLoadingUploadsProject || isCreatingFont || isUploading}
            size="lg"
            variant="primary"
          >
            {isCreatingFont || isUploading ? `Uploading ${pluralize('File', files.length)}...` : 'Upload Away!'}
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
